<script setup lang="ts">
import { trimmedMaybe } from "../lib/trimmedString";
import {
  atIndexMaybe,
  fromMaybe,
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  useTemplateRef,
} from "vue";
import { useRouter } from "vue-router";
import {
  createNewProject,
  deleteLocalProject,
  getRecentProjects,
  listLocalProjects,
  openFolderProject,
  openProjectPath,
  pickProjectParentFolder,
  removeRecentProject,
} from "../lib/tauri/project";
import { isMobileShell } from "../lib/tauri/core";
import { isTauri } from "../lib/runtime";
import { listenMenuCommands } from "../lib/menuCommand";
import { applyEditHistory } from "../lib/editHistoryMenu";
import {
  EDIT_REDO_MENU_ID,
  EDIT_UNDO_MENU_ID,
} from "../lib/editHistorySurface";
import type { UnlistenFn } from "@tauri-apps/api/event";
import { cloudProjectRoot, listCloudProjects } from "../lib/platform";
import AccountPanel from "./AccountPanel.vue";
import ZwFlyout from "./ui/ZwFlyout.vue";
import {
  canRestoreFromCloud,
  listRestorableCloudProjects,
  restoreCloudProjectToDisk,
  type CloudProjectSummary,
} from "../lib/cloudRestore";
import { cloudBackupNeedsSignIn, getCloudSession } from "../lib/cloudAuth";
import { CLOUD_AUTH_EVENT } from "../lib/cloudAuthDeepLink";
import { assertNever } from "../lib/assertNever";
import { canAccessWorkspace } from "../lib/workspaceAccess";
import { useTea } from "../lib/tea";
import { useShellWidth } from "../composables/useShellWidth";
import {
  initialSplashPageModel,
  splashChromeBlocksShortcuts,
  splashMobileLayout,
  updateSplashPageFn,
  type SplashAccountSession,
  type SplashCloudProject,
  type SplashPageCmd,
  type SplashRecentItem,
} from "./SplashPage.update";
import ZwButton from "./ui/ZwButton.vue";
import ZwDialog from "./ui/ZwDialog.vue";
import AboutPanel from "./AboutPanel.vue";
import { aboutHostFor } from "./AboutPanel.update";
import { openAboutWindow } from "../lib/tauri/about";
import { aboutAriaLabel, appVersion, versionLabel } from "../lib/appVersion";
import ZwInput from "./ui/ZwInput.vue";
import logoUrl from "../assets/logo.png";
import {
  dismissBetaVersion,
  ensureBetaSourceRoot,
  probeBetaUpdateOffer,
  startBetaUpdate,
} from "../lib/betaUpdate";

// Splash shows a short list; File > "Open Recent Projects" shows the full backend list.
const SPLASH_RECENTS_LIMIT = 3;

const router = useRouter();
const projectInput =
  useTemplateRef<InstanceType<typeof ZwInput>>("projectInput");

/** Cloud auth returns wire null; convert once at this edge via `toMaybe`. */
function sessionToSplash(
  session: ReturnType<typeof getCloudSession>,
): Maybe<SplashAccountSession> {
  return toMaybe(session);
}

function toSplashCloudProject(p: CloudProjectSummary): SplashCloudProject {
  return {
    id: p.id,
    name: p.name,
    clientProjectId: toMaybe(p.clientProjectId),
    lastBackedUpAt: toMaybe(p.lastBackedUpAt),
  };
}

function fromSplashCloudProject(p: SplashCloudProject): CloudProjectSummary {
  return {
    id: p.id,
    name: p.name,
    clientProjectId: fromMaybe(p.clientProjectId),
    lastBackedUpAt: fromMaybe(p.lastBackedUpAt),
  };
}

async function runSplashCmd(cmd: SplashPageCmd): Promise<void> {
  switch (cmd.scope) {
    case "newProject": {
      const createCmd = cmd.cmd;
      void dispatch({
        type: "BusyStarted",
        label: "Creating…",
        clearError: false,
      });
      try {
        const parent = await pickProjectParentFolder();
        const parentPath = trimmedMaybe(parent);
        if (isNothing(parentPath)) {
          void dispatch({
            type: "NewProject",
            msg: { type: "CreateAborted" },
          });
          return;
        }
        await createNewProject(createCmd.name, parentPath.value, true);
        void dispatch({
          type: "NewProject",
          msg: { type: "CreateSucceeded" },
        });
      } catch (error) {
        console.error("Failed to create project", error);
        void dispatch({
          type: "NewProject",
          msg: {
            type: "CreateFailed",
            error: formatError(error, "Could not create project."),
          },
        });
      } finally {
        void dispatch({ type: "BusyEnded" });
      }
      return;
    }
    case "delete": {
      const deleteCmd = cmd.cmd;
      void dispatch({
        type: "BusyStarted",
        label: "Deleting…",
        clearError: true,
      });
      try {
        const library = await deleteLocalProject(deleteCmd.path);
        void dispatch({
          type: "RecentsLoaded",
          recents: model.value.recents,
          library,
        });
        void dispatch({
          type: "Delete",
          msg: { type: "DeleteSucceeded" },
        });
      } catch (error) {
        console.error("Failed to delete local project", error);
        void dispatch({
          type: "SplashErrorSet",
          error: formatError(error, "Could not delete that project."),
        });
        await refreshRecents();
        void dispatch({
          type: "Delete",
          msg: { type: "DeleteFailed" },
        });
      } finally {
        void dispatch({ type: "BusyEnded" });
      }
      return;
    }
    case "cloudRestore": {
      const restoreCmd = cmd.cmd;
      switch (restoreCmd.kind) {
        case "listProjects": {
          try {
            const listed = await listRestorableCloudProjects();
            const projects = listed.projects.map(toSplashCloudProject);
            let emptyHint = "";
            if (projects.length === 0) {
              emptyHint =
                listed.alreadyLocalCount > 0
                  ? model.value.nativeMobileShell
                    ? "All your backed-up projects are already on this device."
                    : "All your backed-up projects are already in Recents."
                  : "No backed-up projects yet. Turn on cloud backup, then try again.";
            }
            const onlyProject = atIndexMaybe(projects, 0);
            void dispatch({
              type: "CloudRestore",
              msg: {
                type: "Listed",
                projects,
                emptyHint,
                autoSelectId: isJust(onlyProject)
                  ? just(onlyProject.value.id)
                  : nothing,
              },
            });
          } catch (error) {
            console.error("Failed to list cloud projects", error);
            void dispatch({
              type: "CloudRestore",
              msg: {
                type: "ListFailed",
                error: formatError(error, "Could not load cloud projects."),
              },
            });
          }
          return;
        }
        case "restore": {
          const project = toMaybe(
            model.value.cloudProjects.find(
              (p) => p.id === restoreCmd.projectId,
            ),
          );
          if (isNothing(project)) {
            void dispatch({
              type: "CloudRestore",
              msg: {
                type: "RestoreFailed",
                error: "Select a project to restore.",
              },
            });
            return;
          }
          void dispatch({
            type: "BusyStarted",
            label: "Restoring…",
            clearError: false,
          });
          try {
            if (isTauri() === false) {
              const rootPath = cloudProjectRoot(project.value.id);
              void dispatch({
                type: "CloudRestore",
                msg: { type: "RestoreSucceeded" },
              });
              await router.push({
                path: "/",
                query: { project: rootPath },
              });
              return;
            }
            const ok = await restoreCloudProjectToDisk(
              fromSplashCloudProject(project.value),
              true,
            );
            if (ok === false) {
              void dispatch({
                type: "CloudRestore",
                msg: { type: "RestoreAborted" },
              });
              return;
            }
            void dispatch({
              type: "CloudRestore",
              msg: { type: "RestoreSucceeded" },
            });
          } catch (error) {
            console.error("Failed to restore cloud project", error);
            void dispatch({
              type: "CloudRestore",
              msg: {
                type: "RestoreFailed",
                error: formatError(error, "Could not restore that project."),
              },
            });
          } finally {
            void dispatch({ type: "BusyEnded" });
          }
          return;
        }
        default:
          return assertNever(restoreCmd);
      }
    }
    case "about":
      try {
        await openAboutWindow(cmd.cmd.check);
      } catch (error) {
        console.error("Failed to open About window", error);
      }
      return;
    default:
      return assertNever(cmd);
  }
}

const { model, dispatch } = useTea(
  initialSplashPageModel({
    accountSession: sessionToSplash(getCloudSession()),
    cloudReauthNeeded: cloudBackupNeedsSignIn(),
    initialWidth: typeof window === "undefined" ? 0 : window.innerWidth,
  }),
  updateSplashPageFn,
  { runCmd: just(runSplashCmd) },
);

/**
 * Phone layout (ZW-276): the native shell or a narrow window. Layout only:
 * spacing, the account flyout, and always-visible row actions. Anything that
 * touches files or native dialogs (the project library, in-app creation, the
 * folder picker, delete vs remove from recents) reads
 * `model.nativeMobileShell` instead, because a narrow desktop window still
 * has a file system and a folder picker.
 */
const mobileLayout = computed(() => splashMobileLayout(model.value));

/** Home page version label → About (#350). No release date here. */
const appVersionText = versionLabel(appVersion());
const appVersionAriaLabel = aboutAriaLabel(appVersion());

function openAbout(): void {
  void dispatch({ type: "About", msg: { type: "Open", check: false } });
}

function closeAbout(): void {
  void dispatch({ type: "About", msg: { type: "Close" } });
}
const splashRoot = useTemplateRef<HTMLElement>("splashRoot");
useShellWidth(
  () => toMaybe(splashRoot.value),
  (width) => {
    void dispatch({ type: "SplashWidthMeasured", width });
  },
);

const projectName = computed({
  get: (): string => model.value.newProject.draft,
  set: (draft: string): void => {
    void dispatch({ type: "NewProject", msg: { type: "DraftChanged", draft } });
  },
});

const isApple = computed(
  () =>
    /Mac|iPhone|iPad|iPod/.test(navigator.platform) ||
    navigator.userAgent.includes("Mac"),
);
const modLabel = computed(() => (isApple.value ? "⌘" : "Ctrl+"));
const continueItems = computed(() =>
  model.value.nativeMobileShell ? model.value.library : model.value.recents,
);
const hasContinueList = computed(() => continueItems.value.length > 0);
const continueHeading = computed(() =>
  model.value.nativeMobileShell ? "Your projects" : "Continue",
);
const canChooseRestoreLocation = computed(
  () =>
    model.value.busy === false &&
    model.value.cloudRestore.loading === false &&
    model.value.cloudRestore.error === "" &&
    model.value.cloudProjects.length > 0 &&
    matchMaybe(model.value.cloudRestore.selectedId, {
      just: (id) => id.trim().length > 0,
      nothing: () => false,
    }),
);
const restoreDialogDescription = computed(() => {
  if (model.value.cloudRestore.loading) {
    return "Looking up your cloud backups…";
  }
  if (isJust(trimmedMaybe(model.value.cloudRestore.error))) {
    return "Something went wrong loading your backups.";
  }
  if (model.value.cloudProjects.length === 0) {
    return model.value.cloudRestore.emptyHint !== ""
      ? model.value.cloudRestore.emptyHint
      : "Nothing to restore right now.";
  }
  if (isWebApp.value || model.value.nativeMobileShell) {
    return "Choose a project to open in the web editor.";
  }
  return "Choose a project, then a folder on this computer.";
});
const newProjectDescription = computed(() =>
  model.value.nativeMobileShell
    ? "Name your project. It will be saved in this app."
    : "Name it, then choose a folder.",
);
const newProjectPrimaryLabel = computed(() => {
  if (model.value.busy) return "Creating…";
  return model.value.nativeMobileShell ? "Create" : "Choose location…";
});
const restorePrimaryLabel = computed(() => {
  if (model.value.busy) return "Restoring…";
  if (isWebApp.value || model.value.nativeMobileShell) return "Open";
  return "Choose location…";
});
const showRestorePrimary = computed(() => model.value.cloudProjects.length > 0);

const isWebApp = computed(() => isTauri() === false);
const showWebSignIn = computed(
  () => isWebApp.value && isNothing(model.value.accountSession),
);

const showAccountTrigger = computed(
  () =>
    model.value.account.open === false || isJust(model.value.accountSession),
);
const showDesktopNewProject = computed(() => isWebApp.value === false);
const showOpenProjectAction = computed(
  () => model.value.nativeMobileShell === false && isWebApp.value === false,
);
const newProjectActionPrimary = computed(
  () => hasContinueList.value === false && showWebSignIn.value === false,
);
const restoreActionPrimary = computed(
  () =>
    isWebApp.value &&
    isJust(model.value.accountSession) &&
    hasContinueList.value === false,
);
const showBusyStatus = computed(
  () => model.value.busy && model.value.busyLabel !== "",
);
const showSplashError = computed(() => model.value.splashError !== "");
const betaUpdateDialogOpen = computed(() => isJust(model.value.betaUpdate));
const betaUpdateDialogDescription = computed((): Maybe<string> => {
  const update = model.value.betaUpdate;
  if (isNothing(update)) return nothing;
  return just(
    `Version ${update.value.latest} is ready (you have ${update.value.current}). Do you want to update now?`,
  );
});
const betaUpdateCloseOnBackdrop = computed(
  () => model.value.betaUpdateBusy === false,
);
const deleteProjectDialogOpen = computed(() =>
  isJust(model.value.delete.pending),
);
const deleteProjectDialogDescription = computed((): Maybe<string> => {
  const item = model.value.delete.pending;
  if (isNothing(item)) return nothing;
  return just(
    `“${item.value.name}” will be removed from this phone. Cloud backup is unchanged. You can restore it again later.`,
  );
});
const restoreDialogCloseOnBackdrop = computed(() => model.value.busy === false);
const restoreCommitDisabled = computed(
  () => canChooseRestoreLocation.value === false,
);
const accountPanelRequiresAccess = computed(
  () => model.value.workspaceUnlocked === false,
);
const hasProjectError = computed(() => model.value.newProject.error !== "");
const hasBetaUpdateError = computed(() => model.value.betaUpdateError !== "");
const hasCloudProjectsError = computed(
  () => model.value.cloudRestore.error !== "",
);

function backupDateVisible(iso: Maybe<string>): boolean {
  return formatBackupDate(iso) !== "";
}

const accountTriggerLabel = computed(() =>
  matchMaybe(model.value.accountSession, {
    just: (session) => session.email,
    nothing: () =>
      model.value.cloudReauthNeeded ? "Cloud backup paused" : "Account",
  }),
);
const accountTriggerTitle = computed(() => {
  const email = matchMaybe(model.value.accountSession, {
    just: (session) => trimmedMaybe(session.email),
    nothing: (): Maybe<string> => nothing,
  });
  if (isJust(email)) return `${email.value} (${modLabel.value},)`;
  if (model.value.cloudReauthNeeded) {
    return `Cloud backup is paused. Sign in again (${modLabel.value},)`;
  }
  return `Account (${modLabel.value},)`;
});

function formatError(error: unknown, fallback: string): string {
  if (typeof error === "string" && error.trim() !== "") return error;
  if (error instanceof Error && error.message !== "") return error.message;
  return fallback;
}

/** Parent folder hint. Not the full filesystem path. */
function recentLocation(path: string): string {
  const parts = path
    .replace(/\\/g, "/")
    .split("/")
    .filter((part) => part !== "");
  if (parts.length <= 1) return path;
  const parent = parts[parts.length - 2] ?? "";
  const grand = parts[parts.length - 3];
  return isJust(trimmedMaybe(grand)) ? `${grand}/${parent}` : parent;
}

function continueItemMeta(item: SplashRecentItem): string {
  if (isTauri() === false) return "Cloud project";
  if (model.value.nativeMobileShell) return "Project";
  return `Project · ${recentLocation(item.path)}`;
}

async function withBusy(
  label: string,
  work: () => Promise<void>,
): Promise<void> {
  if (model.value.busy) return;
  void dispatch({ type: "BusyStarted", label, clearError: true });
  try {
    await work();
  } finally {
    void dispatch({ type: "BusyEnded" });
  }
}

async function refreshRecents(): Promise<void> {
  if (isTauri() === false) {
    if (getCloudSession() === null) {
      void dispatch({ type: "RecentsLoaded", recents: [], library: [] });
      return;
    }
    try {
      const projects = await listCloudProjects();
      const recents = projects
        .slice(0, SPLASH_RECENTS_LIMIT)
        .map((project) => ({
          path: cloudProjectRoot(project.id),
          name: project.name,
          kind: "project" as const,
        }));
      void dispatch({ type: "RecentsLoaded", recents, library: [] });
    } catch (error) {
      console.error("Failed to load cloud projects", error);
      void dispatch({ type: "RecentsLoaded", recents: [], library: [] });
    }
    return;
  }
  try {
    if (model.value.nativeMobileShell) {
      const library = await listLocalProjects();
      void dispatch({ type: "RecentsLoaded", recents: [], library });
    } else {
      const recents = (await getRecentProjects()).slice(
        0,
        SPLASH_RECENTS_LIMIT,
      );
      void dispatch({ type: "RecentsLoaded", recents, library: [] });
    }
  } catch (error) {
    console.error("Failed to load recent projects", error);
    void dispatch({ type: "RecentsLoaded", recents: [], library: [] });
  }
}

async function requireWorkspaceAccess(): Promise<boolean> {
  const unlocked = await canAccessWorkspace();
  await dispatch({ type: "WorkspaceAccessChecked", unlocked });
  if (unlocked) return true;
  void dispatch({
    type: "SplashErrorSet",
    error: "Sign in or activate a license to continue.",
  });
  openAccount();
  return false;
}

function openNewProjectDialog(): void {
  if (model.value.busy || model.value.newProject.busy) return;
  void (async () => {
    if ((await requireWorkspaceAccess()) === false) return;
    void dispatch({ type: "NewProject", msg: { type: "Open" } });
    void nextTick(() => {
      projectInput.value?.focus();
      projectInput.value?.select();
    });
  })();
}

function cancelNewProject(): void {
  void dispatch({ type: "NewProject", msg: { type: "Cancel" } });
}

function commitNewProject(): void {
  if (model.value.busy) return;
  void (async () => {
    if ((await requireWorkspaceAccess()) === false) {
      void dispatch({ type: "NewProject", msg: { type: "Cancel" } });
      return;
    }
    await dispatch({ type: "NewProject", msg: { type: "Commit" } });
    if (model.value.newProject.error.length > 0) {
      projectInput.value?.focus();
    }
  })();
}

async function onOpenProject(): Promise<void> {
  if ((await requireWorkspaceAccess()) === false) return;
  await withBusy("Opening…", async () => {
    try {
      await openFolderProject(true);
      await refreshRecents();
    } catch (error) {
      console.error("Failed to open project", error);
      void dispatch({
        type: "SplashErrorSet",
        error: formatError(error, "Could not open that project."),
      });
    }
  });
}

async function onOpenRecent(path: string): Promise<void> {
  if ((await requireWorkspaceAccess()) === false) return;
  if (isTauri() === false) {
    await withBusy("Opening…", async () => {
      await router.push({
        path: "/",
        query: { project: path },
      });
    });
    return;
  }
  await withBusy("Opening…", async () => {
    try {
      await openProjectPath(path, true);
    } catch (error) {
      console.error("Failed to open recent item", error);
      void dispatch({
        type: "SplashErrorSet",
        error: formatError(error, "Could not open that item."),
      });
      await refreshRecents();
    }
  });
}

async function onRemoveRecent(path: string): Promise<void> {
  if (model.value.busy) return;
  void dispatch({ type: "SplashErrorCleared" });
  try {
    const recents = (await removeRecentProject(path)).slice(
      0,
      SPLASH_RECENTS_LIMIT,
    );
    void dispatch({
      type: "RecentsLoaded",
      recents,
      library: model.value.library,
    });
  } catch (error) {
    console.error("Failed to remove recent item", error);
    void dispatch({
      type: "SplashErrorSet",
      error: formatError(error, "Could not update recent items."),
    });
    await refreshRecents();
  }
}

function askDeleteLocalProject(item: SplashRecentItem): void {
  if (model.value.busy) return;
  void dispatch({
    type: "Delete",
    msg: {
      type: "Ask",
      target: { path: item.path, name: item.name },
    },
  });
}

function cancelDeleteLocalProject(): void {
  void dispatch({ type: "Delete", msg: { type: "Cancel" } });
}

function commitDeleteLocalProject(): void {
  if (model.value.busy === true) return;
  void dispatch({ type: "Delete", msg: { type: "Commit" } });
}

function openAccount(): void {
  if (model.value.busy) return;
  void dispatch({ type: "Account", msg: { type: "Open" } });
}

function closeAccount(): void {
  void dispatch({ type: "Account", msg: { type: "Close" } });
}

function onAccountFlyoutClosed(): void {
  void dispatch({ type: "Account", msg: { type: "FlyoutClosed" } });
}

function onAccountAuthChanged(): void {
  void refreshCloudAuth();
}

async function refreshCloudAuth(): Promise<void> {
  const sessionBefore = getCloudSession();
  const unlocked = await canAccessWorkspace();
  await dispatch({ type: "WorkspaceAccessChecked", unlocked });
  const restoreAvailable =
    unlocked === true && isJust(sessionToSplash(sessionBefore))
      ? await canRestoreFromCloud()
      : false;
  // The entitlement check above is where a dead session usually surfaces, so
  // re-read instead of trusting the snapshot from the top of this pass.
  const session = getCloudSession();
  void dispatch({
    type: "CloudAuthSynced",
    session: sessionToSplash(session),
    workspaceUnlocked: await canAccessWorkspace(),
    cloudRestoreAvailable: restoreAvailable,
    cloudReauthNeeded: cloudBackupNeedsSignIn(),
  });
  if (isTauri() === false) {
    await refreshRecents();
  }
}

async function openRestoreFromCloud(): Promise<void> {
  if (model.value.busy) return;
  if ((await requireWorkspaceAccess()) === false) return;
  void dispatch({ type: "CloudRestore", msg: { type: "Open" } });
}

function cancelRestoreFromCloud(): void {
  void dispatch({ type: "CloudRestore", msg: { type: "Cancel" } });
}

function selectCloudProject(id: string): void {
  void dispatch({ type: "CloudRestore", msg: { type: "Select", id } });
}

function formatBackupDate(iso: Maybe<string>): string {
  if (isNothing(iso) || isNothing(trimmedMaybe(iso.value))) return "";
  try {
    const d = new Date(iso.value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function commitRestoreFromCloud(): void {
  if (model.value.busy) return;
  void dispatch({ type: "CloudRestore", msg: { type: "Commit" } });
}

function onKeydown(event: KeyboardEvent): void {
  if (splashChromeBlocksShortcuts(model.value)) {
    return;
  }
  const mod = event.metaKey || event.ctrlKey;
  if (mod === false || event.altKey === true) return;
  const key = event.key.toLowerCase();
  if (key === "n" && event.shiftKey === false) {
    event.preventDefault();
    openNewProjectDialog();
  } else if (key === "o" && event.shiftKey === false) {
    event.preventDefault();
    if (model.value.nativeMobileShell === false) void onOpenProject();
  } else if (key === ",") {
    event.preventDefault();
    openAccount();
  }
}

function dismissBetaUpdate(): void {
  matchMaybe(model.value.betaUpdate, {
    just: (info) => {
      dismissBetaVersion(info.latest);
    },
    nothing: () => {},
  });
  void dispatch({ type: "BetaUpdateDismissed" });
}

async function confirmBetaUpdate(): Promise<void> {
  if (
    isNothing(model.value.betaUpdate) ||
    model.value.betaUpdateBusy === true
  ) {
    return;
  }
  void dispatch({ type: "BetaUpdateConfirmStarted" });
  try {
    const root = await ensureBetaSourceRoot();
    if (isNothing(root)) {
      void dispatch({
        type: "BetaUpdateErrorSet",
        error: "Select your zw-app checkout folder to continue the update.",
      });
      return;
    }
    await startBetaUpdate();
    // App should exit; if it does not, surface a hint.
    void dispatch({
      type: "BetaUpdateErrorSet",
      error:
        "Update started in Terminal. Quit Zephyrwrite if it is still open.",
    });
  } catch (error) {
    void dispatch({
      type: "BetaUpdateErrorSet",
      error: error instanceof Error ? error.message : String(error),
    });
  } finally {
    void dispatch({ type: "BetaUpdateConfirmEnded" });
  }
}

let unlistenMenuCommands: Maybe<UnlistenFn> = nothing;
let splashAlive = false;

/** Splash has no manuscript editor. Text fields are handled inside applyEditHistory. */
function splashHasNoEditor(): void {
  return;
}

onMounted(() => {
  splashAlive = true;
  // Kick off immediately in parallel with splash boot. Never await: recents
  // and cloud auth must paint even if the check is slow.
  if (isTauri()) {
    void probeBetaUpdateOffer().then((info) => {
      if (splashAlive === false) return;
      matchMaybe(info, {
        just: (offer) => {
          void dispatch({ type: "BetaUpdateOffered", info: offer });
        },
        nothing: () => {},
      });
    });
  }
  void (async () => {
    const mobileShell = await isMobileShell();
    void dispatch({ type: "MobileShellDetected", mobileShell });
    void dispatch({
      type: "About",
      msg: { type: "HostDetected", host: aboutHostFor(isTauri(), mobileShell) },
    });
    await refreshRecents();
    await refreshCloudAuth();
    if (isTauri()) {
      try {
        unlistenMenuCommands = just(
          await listenMenuCommands({
            "file-new-project": () => openNewProjectDialog(),
            [EDIT_UNDO_MENU_ID]: () => {
              applyEditHistory("undo", splashHasNoEditor);
            },
            [EDIT_REDO_MENU_ID]: () => {
              applyEditHistory("redo", splashHasNoEditor);
            },
          }),
        );
      } catch (error) {
        console.warn("Menu command listener unavailable", error);
      }
    }
  })();
  window.addEventListener("keydown", onKeydown);
  window.addEventListener("focus", refreshCloudAuth);
  window.addEventListener(CLOUD_AUTH_EVENT, refreshCloudAuth);
});

onUnmounted(() => {
  splashAlive = false;
  matchMaybe(unlistenMenuCommands, {
    just: (fn) => {
      fn();
    },
    nothing: () => {},
  });
  unlistenMenuCommands = nothing;
  window.removeEventListener("keydown", onKeydown);
  window.removeEventListener("focus", refreshCloudAuth);
  window.removeEventListener(CLOUD_AUTH_EVENT, refreshCloudAuth);
});
</script>

<template>
  <div
    ref="splashRoot"
    class="splash zw-paper-surface"
    :class="{ 'splash-account-active': model.account.present }"
  >
    <button
      v-if="showAccountTrigger"
      type="button"
      class="splash-account-trigger"
      :class="{
        'splash-account-trigger-open': model.account.open,
        'splash-account-trigger__mobile': mobileLayout,
        'splash-account-trigger-alert': model.cloudReauthNeeded,
      }"
      :title="accountTriggerTitle"
      aria-controls="splash-account-flyout"
      :aria-expanded="model.account.open"
      @click="model.account.open ? closeAccount() : openAccount()"
    >
      <span
        v-if="model.cloudReauthNeeded"
        class="splash-account-flag"
        aria-hidden="true"
      />
      {{ accountTriggerLabel }}
    </button>

    <div
      class="splash-body"
      :class="{
        'splash-body-return': hasContinueList,
        'splash-body__mobile': mobileLayout,
      }"
    >
      <header class="splash-brand">
        <img
          class="splash-brand-mark"
          :src="logoUrl"
          width="56"
          height="56"
          alt=""
        />
        <h1>Zephyrwrite</h1>
      </header>

      <section
        v-if="hasContinueList"
        class="splash-recents"
        :aria-label="continueHeading"
      >
        <h2>{{ continueHeading }}</h2>
        <ul>
          <li
            v-for="item in continueItems"
            :key="item.path"
            class="splash-recent-row"
          >
            <button
              type="button"
              class="splash-recent"
              :disabled="model.busy"
              :title="model.nativeMobileShell ? item.name : item.path"
              @click="onOpenRecent(item.path)"
            >
              <span class="splash-recent-name">{{ item.name }}</span>
              <span class="splash-recent-meta">{{
                continueItemMeta(item)
              }}</span>
            </button>
            <button
              type="button"
              class="splash-recent-remove"
              :class="{ 'splash-recent-remove-visible': mobileLayout }"
              :disabled="model.busy"
              :title="
                model.nativeMobileShell
                  ? `Delete ${item.name} from this device`
                  : `Remove ${item.name} from recent`
              "
              :aria-label="
                model.nativeMobileShell
                  ? `Delete ${item.name} from this device`
                  : `Remove ${item.name} from recent`
              "
              @click.stop="
                model.nativeMobileShell
                  ? askDeleteLocalProject(item)
                  : onRemoveRecent(item.path)
              "
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.75"
                class="size-3.5"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 6l12 12M18 6 6 18"
                />
              </svg>
            </button>
          </li>
        </ul>
      </section>

      <nav
        class="splash-actions"
        :class="{ 'splash-actions-secondary': hasContinueList }"
        aria-label="Project actions"
      >
        <button
          v-if="showWebSignIn"
          type="button"
          class="splash-action splash-action-primary"
          :disabled="model.busy"
          title="Sign in with email"
          @click="openAccount"
        >
          Sign in
        </button>
        <button
          v-if="showDesktopNewProject"
          type="button"
          class="splash-action"
          :class="{
            'splash-action-primary': newProjectActionPrimary,
          }"
          :disabled="model.busy"
          :title="`New project (${modLabel}N)`"
          @click="openNewProjectDialog"
        >
          New project
        </button>
        <button
          v-if="showOpenProjectAction"
          type="button"
          class="splash-action"
          :disabled="model.busy"
          :title="`Open project (${modLabel}O)`"
          @click="onOpenProject"
        >
          Open project
        </button>
        <button
          v-if="model.cloudRestoreAvailable"
          type="button"
          class="splash-action"
          :class="{
            'splash-action-primary': restoreActionPrimary,
          }"
          :disabled="model.busy"
          title="Restore a project from cloud backup"
          @click="openRestoreFromCloud"
        >
          Restore from cloud
        </button>
        <button
          v-if="isWebApp"
          type="button"
          class="splash-action"
          :disabled="model.busy"
          title="Try the demo manuscript without signing in"
          @click="router.push('/?demo=1')"
        >
          Try demo
        </button>

        <p
          v-if="showBusyStatus"
          class="splash-status"
          role="status"
          aria-live="polite"
        >
          {{ model.busyLabel }}
        </p>
        <p v-else-if="showSplashError" class="splash-error" role="alert">
          {{ model.splashError }}
        </p>
      </nav>
    </div>

    <ZwDialog
      :open="betaUpdateDialogOpen"
      title="Update available"
      :description="betaUpdateDialogDescription"
      :close-on-backdrop="betaUpdateCloseOnBackdrop"
      @close="dismissBetaUpdate"
    >
      <p v-if="hasBetaUpdateError" class="zw-dialog-error">
        {{ model.betaUpdateError }}
      </p>
      <template #footer>
        <ZwButton :disabled="model.betaUpdateBusy" @click="dismissBetaUpdate">
          Later
        </ZwButton>
        <ZwButton
          variant="primary"
          :disabled="model.betaUpdateBusy"
          @click="confirmBetaUpdate"
        >
          Update
        </ZwButton>
      </template>
    </ZwDialog>

    <ZwDialog
      :open="model.newProject.open"
      title="New project"
      :description="just(newProjectDescription)"
      @close="cancelNewProject"
    >
      <ZwInput
        :id="nothing"
        ref="projectInput"
        v-model="projectName"
        :label="just('Project name')"
        placeholder="Project name"
        :disabled="model.busy"
        @keydown.enter.prevent="commitNewProject"
      />
      <p v-if="hasProjectError" class="zw-dialog-error">
        {{ model.newProject.error }}
      </p>
      <template #footer>
        <ZwButton :disabled="model.busy" @click="cancelNewProject">
          Cancel
        </ZwButton>
        <ZwButton
          variant="primary"
          :disabled="model.busy"
          @click="commitNewProject"
        >
          {{ newProjectPrimaryLabel }}
        </ZwButton>
      </template>
    </ZwDialog>

    <ZwDialog
      :open="deleteProjectDialogOpen"
      title="Delete from this device?"
      :description="deleteProjectDialogDescription"
      :close-on-backdrop="restoreDialogCloseOnBackdrop"
      @close="cancelDeleteLocalProject"
    >
      <template #footer>
        <ZwButton :disabled="model.busy" @click="cancelDeleteLocalProject">
          Cancel
        </ZwButton>
        <ZwButton
          variant="primary"
          :disabled="model.busy"
          @click="commitDeleteLocalProject"
        >
          Delete
        </ZwButton>
      </template>
    </ZwDialog>

    <ZwDialog
      :open="model.cloudRestore.open"
      title="Restore from cloud"
      :description="just(restoreDialogDescription)"
      :close-on-backdrop="restoreDialogCloseOnBackdrop"
      @close="cancelRestoreFromCloud"
    >
      <ul
        v-if="model.cloudProjects.length > 0"
        class="splash-cloud-list"
        role="listbox"
        aria-label="Cloud projects"
      >
        <li v-for="project in model.cloudProjects" :key="project.id">
          <button
            type="button"
            class="splash-cloud-item"
            role="option"
            :aria-selected="
              isJust(model.cloudRestore.selectedId) &&
              model.cloudRestore.selectedId.value === project.id
            "
            :disabled="model.busy"
            :data-selected="
              isJust(model.cloudRestore.selectedId) &&
              model.cloudRestore.selectedId.value === project.id
            "
            @click="selectCloudProject(project.id)"
            @dblclick="
              selectCloudProject(project.id);
              commitRestoreFromCloud();
            "
          >
            <span class="splash-cloud-name">{{ project.name }}</span>
            <span
              v-if="backupDateVisible(project.lastBackedUpAt)"
              class="splash-cloud-meta"
            >
              Backed up ·
              {{ formatBackupDate(project.lastBackedUpAt) }}
            </span>
          </button>
        </li>
      </ul>
      <p v-else-if="model.cloudRestore.loading" class="splash-cloud-status">
        Loading…
      </p>
      <p v-if="hasCloudProjectsError" class="zw-dialog-error">
        {{ model.cloudRestore.error }}
      </p>
      <template #footer>
        <ZwButton :disabled="model.busy" @click="cancelRestoreFromCloud">
          {{ showRestorePrimary ? "Cancel" : "Done" }}
        </ZwButton>
        <ZwButton
          v-if="showRestorePrimary"
          variant="primary"
          :disabled="restoreCommitDisabled"
          @click="commitRestoreFromCloud"
        >
          {{ restorePrimaryLabel }}
        </ZwButton>
      </template>
    </ZwDialog>

    <button
      type="button"
      class="splash-version"
      :class="{ 'splash-version__mobile': mobileLayout }"
      data-testid="splash-version"
      :aria-label="appVersionAriaLabel"
      @click="openAbout"
    >
      {{ appVersionText }}
    </button>

    <ZwDialog
      :open="model.about.open"
      title="About Zephyrwrite"
      :description="nothing"
      :show-close="true"
      @close="closeAbout"
    >
      <AboutPanel host="dialog" :check-on-open="model.about.checkOnOpen" />
    </ZwDialog>

    <ZwFlyout
      :id="just('splash-account-flyout')"
      :labelled-by="just('account-title')"
      :label="nothing"
      :open="model.account.open"
      side="left"
      :close-on-backdrop="true"
      :z-index="40"
      width="min(100%, 22rem)"
      height="50dvh"
      :flush="false"
      :full-bleed="mobileLayout"
      initial-focus="#account-email, .account-done, input:not([disabled])"
      @close="closeAccount"
      @closed="onAccountFlyoutClosed"
    >
      <AccountPanel
        :require-access="accountPanelRequiresAccess"
        @close="closeAccount"
        @auth-changed="onAccountAuthChanged"
      />
    </ZwFlyout>
  </div>
</template>

<style scoped>
.splash {
  position: relative;
  display: grid;
  height: 100%;
  min-height: 100%;
  place-items: stretch;
  overflow: hidden;
  color: var(--text);
}

.splash-body {
  transition: opacity var(--duration-med) var(--ease-out-quart);
}

.splash-account-active .splash-body {
  opacity: 0.55;
}

@media (prefers-reduced-motion: reduce) {
  .splash-body {
    transition: none;
  }
}

.splash-account-trigger {
  position: absolute;
  top: var(--space-lg);
  right: var(--space-lg);
  z-index: 2;
  appearance: none;
  border: 0;
  background: transparent;
  max-width: min(16rem, calc(100vw - 3.5rem));
  padding: 0.35rem 0.45rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
  font-family: var(--font-ui);
  font-size: 0.85rem;
  letter-spacing: 0.02em;
  color: color-mix(in oklch, var(--text-muted) 72%, transparent);
  cursor: pointer;
  border-radius: 0.15rem;
  transition: color var(--duration-fast) var(--ease-out-quart);
}

.splash-account-trigger:hover {
  color: var(--text-strong);
}

.splash-account-trigger:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}

/* Version label (#350): quiet, bottom-right, opens About. */
.splash-version {
  position: absolute;
  right: 16px;
  bottom: 16px;
  z-index: 2;
  appearance: none;
  border: 0;
  background: transparent;
  min-height: 24px;
  min-width: 24px;
  padding: 0.2rem 0.35rem;
  border-radius: 0.3rem;
  font-family: var(--font-ui);
  font-size: 0.75rem;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  color: var(--text-muted);
  cursor: pointer;
  transition: color var(--duration-fast) var(--ease-out-quart);
}

.splash-version:hover {
  color: var(--text-strong);
}

.splash-version:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}

.splash-version__mobile {
  right: calc(12px + env(safe-area-inset-right, 0px));
  bottom: calc(12px + env(safe-area-inset-bottom, 0px));
}

.splash-account-trigger__mobile {
  top: var(--space-3xl);
}

.splash-account-trigger-open {
  z-index: 50;
}

/** Signed out of cloud backup: warmer than the resting label, still a whisper. */
.splash-account-trigger-alert {
  color: color-mix(in oklch, var(--color-zw-amber) 72%, var(--text-strong));
}

.splash-account-flag {
  display: inline-block;
  width: 0.4rem;
  height: 0.4rem;
  margin-right: 0.35rem;
  vertical-align: 0.05rem;
  border-radius: 999px;
  background: var(--color-zw-amber);
}

.splash-body {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--space-2xl);
  width: min(100% - 2.5rem, 22rem);
  margin: 0;
  margin-left: clamp(1.75rem, 10vw, 4.5rem);
  padding: var(--space-3xl) 0;
  min-height: 100%;
  box-sizing: border-box;
}

.splash-body__mobile {
  margin: 0 auto;
}

.splash-body-return {
  gap: var(--space-xl);
}

.splash-brand {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.85rem;
}

.splash-brand-mark {
  width: clamp(2.75rem, 8vw, 3.5rem);
  height: clamp(2.75rem, 8vw, 3.5rem);
  border-radius: 0.65rem;
  display: block;
  flex-shrink: 0;
}

.splash-brand h1 {
  margin: 0;
  font-family: var(--font-heading);
  font-size: clamp(2.85rem, 9vw, 3.65rem);
  font-weight: 400;
  line-height: 1;
  letter-spacing: -0.04em;
  color: var(--splash-title);
}

/*
 * Phone layout: a smaller wordmark that shrinks with the screen, so the
 * logo fits inside the side padding down to a 360px phone (iPhone 12 mini).
 * The mark keeps the desktop ratio to the text.
 */
.splash-body__mobile .splash-brand {
  --splash-brand-size: clamp(1.75rem, 11.3vw, 2.6rem);
  gap: calc(var(--splash-brand-size) * 0.3);
}

.splash-body__mobile .splash-brand-mark {
  width: calc(var(--splash-brand-size) * 0.96);
  height: calc(var(--splash-brand-size) * 0.96);
}

.splash-body__mobile .splash-brand h1 {
  font-size: var(--splash-brand-size);
  white-space: nowrap;
}

.splash-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  font-family: var(--font-ui);
}

.splash-actions-secondary {
  gap: 0.2rem;
  padding-top: var(--space-xs);
}

.splash-action {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0.15rem 0;
  font: inherit;
  font-size: 1.12rem;
  line-height: 1.4;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: 0.15rem;
  transition: color var(--duration-fast) var(--ease-out-quart);
}

.splash-action:hover:not(:disabled) {
  color: var(--text-strong);
}

.splash-action:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.splash-action:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 3px;
}

.splash-action-primary {
  font-size: 1.45rem;
  color: var(--text-strong);
}

.splash-actions-secondary .splash-action {
  font-size: 1rem;
  color: color-mix(in oklch, var(--text-muted) 96%, var(--text-strong));
}

.splash-status,
.splash-error {
  margin: 0.35rem 0 0;
  font-size: 0.88rem;
  line-height: 1.35;
  max-width: 18rem;
}

.splash-status {
  color: color-mix(in oklch, var(--text-muted) 90%, transparent);
}

.splash-error {
  color: var(--danger);
}

.splash-cloud-status {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.45;
  color: var(--text-muted);
}

.splash-cloud-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: 16rem;
  overflow: auto;
  border: 1px solid var(--line);
  border-radius: 0.4rem;
  background: var(--bg);
}

.splash-cloud-item {
  appearance: none;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  margin: 0;
  padding: 0.7rem 0.85rem;
  border: 0;
  border-bottom: 1px solid var(--line);
  background: transparent;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: var(--text);
}

.splash-cloud-list li:last-child .splash-cloud-item {
  border-bottom: 0;
}

.splash-cloud-item:hover:not(:disabled) {
  background: var(--hover-fill);
}

.splash-cloud-item[data-selected="true"] {
  background: color-mix(in oklch, var(--text-strong) 8%, var(--bg));
}

.splash-cloud-name {
  font-size: 1.02rem;
  color: var(--text-strong);
}

.splash-cloud-meta {
  font-size: 0.88rem;
  color: var(--text-muted);
}

.splash-recents {
  min-width: 0;
}

.splash-recents h2 {
  margin: 0 0 var(--space-sm);
  font-family: var(--font-ui);
  font-size: 0.82rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: color-mix(in oklch, var(--text-muted) 80%, transparent);
}

.splash-recents ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.splash-recent-row {
  display: flex;
  align-items: stretch;
  gap: 0.15rem;
  min-width: 0;
}

.splash-recent {
  appearance: none;
  border: 0;
  background: transparent;
  flex: 1;
  min-width: 0;
  text-align: left;
  padding: 0.5rem 0.25rem;
  border-radius: 0.25rem;
  cursor: pointer;
  color: inherit;
  font: inherit;
  transition: background var(--duration-fast) var(--ease-out-quart);
}

.splash-recent:hover:not(:disabled) {
  background: color-mix(in oklch, var(--text-muted) 7%, transparent);
}

.splash-recent:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.splash-recent:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 1px;
}

.splash-recent-name {
  display: block;
  font-family: var(--font-prose);
  font-size: 1.12rem;
  color: var(--text-strong);
}

.splash-recent-meta {
  display: block;
  margin-top: 0.12rem;
  font-family: var(--font-ui);
  font-size: 0.82rem;
  color: color-mix(in oklch, var(--text-muted) 85%, transparent);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.splash-recent-remove {
  appearance: none;
  border: 0;
  background: transparent;
  flex-shrink: 0;
  align-self: center;
  width: 1.75rem;
  height: 1.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  color: color-mix(in oklch, var(--text-muted) 65%, transparent);
  cursor: pointer;
  opacity: 0;
  transition:
    opacity var(--duration-fast) var(--ease-out-quart),
    color var(--duration-fast) var(--ease-out-quart),
    background var(--duration-fast) var(--ease-out-quart);
}

.splash-recent-remove-visible {
  opacity: 1;
}

.splash-recent-row:hover .splash-recent-remove,
.splash-recent-row:focus-within .splash-recent-remove {
  opacity: 1;
}

.splash-recent-remove:hover:not(:disabled) {
  color: var(--text-strong);
  background: var(--hover-fill);
}

.splash-recent-remove:disabled {
  cursor: not-allowed;
}

.splash-recent-remove:focus-visible {
  opacity: 1;
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 1px;
}

@media (prefers-reduced-motion: reduce) {
  .splash-account-trigger,
  .splash-action,
  .splash-recent,
  .splash-recent-remove {
    transition: none;
  }
}
</style>
